import { useState, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import { formatDateDMY } from "../lib/ScheduleUtils";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Calendar } from "../components/common/Calendar";
import { Textarea } from "../components/common/Textarea";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import * as utils from "../utils/utils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";

import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";

import {
  AlertCircle,
  ArrowLeft,
  Upload,
  X,
  FileText,
  Save,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./common/Select";
import { fetchTreatments, applyTreatment } from "../apis/api.inventory";
import { queryInventoryTreatmentsKey } from "../apis/queryKeys";
import TreatmentConsumptionDialog from "./inventory/TreatmentConsumptionDialog";

const NO_TREATMENT = "__none__";

export default function ClinicalHistoryForm() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const { addClinicalHistory, getProfileById } = useData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const appointmentId = searchParams.get("appointmentId");
  const patientId = searchParams.get("patientId");
  const doctorId = searchParams.get("doctorId");
  const consultationDateParam = searchParams.get("consultationDate");
  const historyId = searchParams.get("historyId");
  const isDigitizing = searchParams.get("mode") === "digitize";
  const isEditing = !!historyId;

  // Prevenir edición de historias clínicas
  useEffect(() => {
    if (isEditing) {
      customToast(t("app.clinical.noEdit", "Las historias clínicas no se pueden editar. Solo se pueden crear nuevas."), "warning");
      if (patientId) {
        navigate(`/clinical-history/manage?patientId=${patientId}`);
      } else {
        navigate("/");
      }
    }
  }, [isEditing, patientId, navigate]);

  const [patient, setPatient] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [patientError, setPatientError] = useState(null);
  const [patientLoaded, setPatientLoaded] = useState(false); // Nueva bandera para controlar si ya se cargó

  const [notes, setNotes] = useState("");
  const [consultationDate, setConsultationDate] = useState(
    () => consultationDateParam || new Date().toISOString().split("T")[0]
  );
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Tratamiento (inventario): opcional. Al guardar se confirma el consumo y se descuenta stock.
  const [selectedTreatmentId, setSelectedTreatmentId] = useState(NO_TREATMENT);
  const [showConsumption, setShowConsumption] = useState(false);
  const { data: treatments } = useQuery({
    queryKey: queryInventoryTreatmentsKey(doctorId),
    queryFn: () => fetchTreatments(doctorId),
    enabled: !!doctorId && user?.role === "doctor",
  });
  const selectedTreatment =
    selectedTreatmentId !== NO_TREATMENT
      ? (treatments || []).find((tr) => tr.id === selectedTreatmentId) || null
      : null;

  // Ref para controlar si el componente está montado
  const isMountedRef = useRef(true);

  const getFecha = () => formatDateDMY(consultationDate);

  useEffect(() => {
    // Limpiar al desmontar
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchPatient = async () => {
      // Solo ejecutar si tenemos patientId, no hemos cargado ya, y no estamos cargando
      if (!patientId || patientLoaded || loadingPatient) {
        return;
      }

      console.log(`Iniciando carga del paciente: ${patientId}`);
      setLoadingPatient(true);
      setPatientError(null);

      try {
        const patientData = await getProfileById(patientId);

        // Solo actualizar el estado si el componente sigue montado
        if (isMountedRef.current) {
          if (patientData) {
            setPatient(patientData);
            console.log(
              `Paciente cargado exitosamente: ${patientData.name} ${patientData.last_name}`
            );
          } else {
            setPatientError("No se pudo cargar la información del paciente");
          }
          setPatientLoaded(true); // Marcar como cargado
        }
      } catch (error) {
        console.error("Error fetching patient:", error);
        if (isMountedRef.current) {
          setPatientError(
            "Error al cargar los datos del paciente: " + error.message
          );
          setPatientLoaded(true); // Marcar como cargado incluso si hay error
        }
      } finally {
        if (isMountedRef.current) {
          setLoadingPatient(false);
        }
      }
    };

    fetchPatient();
  }, [patientId, patientLoaded, loadingPatient, getProfileById]); // Dependencias controladas

  // La edición de historias clínicas no está permitida

  const uploadFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);

    try {
      const uploadPromises = files.map(async (image) => {
        const formData = new FormData();
        formData.append("image", image);

        const response = await fetch(`/api/utils/upload`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || "Error al subir imagen");
        }

        const result = await response.json();
        return {
          url: result.url,
          filename: image.name,
          originalFile: image,
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      if (isMountedRef.current) {
        setImages((prev) => [...prev, ...uploadedImages]);
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      if (isMountedRef.current) {
        customToast(t("app.clinical.uploadError", "Error al subir las imágenes: {{message}}", { message: error.message }), "danger");
      }
    } finally {
      if (isMountedRef.current) {
        setUploading(false);
      }
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    await uploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Solo cambiar el estado si realmente salimos del área de drop
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(
      (file) => file.type.startsWith("image/")
    );
    
    if (files.length > 0) {
      await uploadFiles(files);
    }
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (isDigitizing) {
      if (images.length === 0) {
        customToast(
          t("app.clinical.digitizeImagesRequired", "Subí al menos una imagen del registro físico"),
          "warning"
        );
        return;
      }
    } else if (!notes.trim()) {
      customToast(t("app.clinical.notesRequired", "Por favor, ingresa las notas de la consulta"), "warning");
      return;
    }

    // Si hay un tratamiento elegido, primero confirmamos el consumo de insumos.
    if (selectedTreatment) {
      setShowConsumption(true);
      return;
    }

    await doSave(null);
  };

  // Guarda la historia clínica y, si corresponde, descuenta el stock del tratamiento.
  const doSave = async (consumedItems) => {
    setSaving(true);

    const notesToSave = notes.trim()
      || (isDigitizing
        ? t("app.clinical.digitizeDefaultNotes", "Registro físico digitalizado")
        : "");

    try {
      // Solo se permiten nuevas historias clínicas, no ediciones
      const result = await addClinicalHistory({
        appointmentId,
        doctorId,
        patientId,
        consultationDate,
        notes: notesToSave,
        imageUrls: images.map((img) => img.url),
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      // Descontar insumos del tratamiento aplicado (si el médico eligió uno).
      if (selectedTreatment && consumedItems) {
        try {
          await applyTreatment(doctorId, {
            treatment_id: selectedTreatment.id,
            patient_id: patientId,
            clinical_history_id: result.data?.id || null,
            items: consumedItems,
          });
          customToast(
            t("inventory.consumption.applied", "Consumo registrado y stock actualizado"),
            "success"
          );
        } catch (invErr) {
          // No bloqueamos el guardado de la historia si falla el descuento de stock.
          customToast(
            invErr?.message || t("inventory.consumption.applyError", "Error al registrar el consumo"),
            "danger"
          );
        }
      }

      customToast(t("app.clinical.saveSuccess", "Historia clínica guardada exitosamente"), "success");
      setShowConsumption(false);
      if (patientId) {
        navigate(`/clinical-history/manage?patientId=${patientId}`);
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Error saving clinical history:", error);
      if (isMountedRef.current) {
        customToast(t("app.clinical.saveError", "Error al guardar la historia clínica: {{message}}", { message: error.message }), "danger");
      }
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  if (!user || user.role !== "doctor") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.appointments.restrictedAccess", "Acceso Restringido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Solo los médicos pueden agregar historias clínicas.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isEditing && (!patientId || !doctorId)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.commonUi.error", "Error")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Faltan parámetros requeridos para crear la historia clínica.
            </p>
            <Button onClick={() => utils.goBack()} className="mt-4">{t("common.backHome", "Volver al Inicio")}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => utils.goBack()}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Consultas
          </Button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isDigitizing
              ? t("app.clinical.digitizeTitle", "Digitalizar Historias")
              : t("app.clinical.newHistoryTitle", "Nueva Historia Clínica")}
          </h1>

          {loadingPatient ? (
            <p className="text-gray-600 flex items-center">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("app.clinical.loadingPatient", "Cargando información del paciente...")}
            </p>
          ) : patientError ? (
            <p className="text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              {patientError}
            </p>
          ) : (
            <p className="text-gray-600">
              {t("app.clinical.patientLabel", "Paciente:")}{" "}
              <strong>
                {patient
                  ? `${patient.name} ${patient.last_name}`
                  : t("app.clinical.patientNotFound", "No encontrado")}
              </strong>
            </p>
          )}

          <div className="text-gray-600">
            {isDigitizing ? (
              <div className="mt-2">
                <Label htmlFor="consultation-date">
                  {t("app.clinical.recordDate", "Fecha del registro:")}
                </Label>
                <Input
                  id="consultation-date"
                  type="date"
                  value={consultationDate}
                  onChange={(e) => setConsultationDate(e.target.value)}
                  className="mt-1 max-w-xs"
                />
              </div>
            ) : (
              <p>
                {t("app.clinical.digitizationDate", "Fecha de digitalización:")}{" "}
                <strong>{getFecha()}</strong>
              </p>
            )}
          </div>
        </div>

        <Card className="mb-20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {isDigitizing
                ? t("app.clinical.digitizeDetails", "Digitalización de registro físico")
                : t("app.clinical.consultationDetails", "Detalles de la Consulta")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Notas de la consulta */}
            <div>
              <Label htmlFor="notes">
                {isDigitizing
                  ? t("app.clinical.digitizeNotes", "Notas (opcional)")
                  : t("app.clinical.consultationNotes", "Notas de la Consulta")}{" "}
                {!isDigitizing && "*"}
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  isDigitizing
                    ? t(
                        "app.clinical.digitizeNotesPlaceholder",
                        "Descripción opcional del registro físico (ej.: páginas digitalizadas, observaciones generales, etc.)"
                      )
                    : t(
                        "app.clinical.notesPlaceholder",
                        "Describe los síntomas, diagnóstico, tratamiento recomendado, observaciones, etc."
                      )
                }
                rows={8}
                className="mt-2"
              />
              <p className="text-sm text-gray-500 mt-1">
                {isDigitizing
                  ? t(
                      "app.clinical.digitizeNotesHint",
                      "Podés agregar una descripción breve. Las fotos o escaneos del registro físico son lo principal."
                    )
                  : t(
                      "app.clinical.notesHint",
                      "Incluye toda la información relevante de la consulta médica."
                    )}
              </p>
            </div>

            {/* Tratamiento (inventario): descuenta stock al guardar */}
            {!isDigitizing && (treatments || []).length > 0 && (
              <div>
                <Label>{t("inventory.consumption.selectTreatment", "Tratamiento (opcional)")}</Label>
                <Select value={selectedTreatmentId} onValueChange={setSelectedTreatmentId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue
                      placeholder={t(
                        "inventory.consumption.selectTreatmentPlaceholder",
                        "Sin tratamiento"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TREATMENT}>
                      {t("inventory.consumption.selectTreatmentPlaceholder", "Sin tratamiento")}
                    </SelectItem>
                    {(treatments || []).map((tr) => (
                      <SelectItem key={tr.id} value={tr.id}>
                        {tr.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subida de imágenes */}
            <div>
              <Label>
                {isDigitizing
                  ? t("app.clinical.digitizeImages", "Imágenes del registro físico *")
                  : t("app.clinical.attachmentsOptional", "Imágenes Adjuntas (Opcional)")}
              </Label>
              <div className="mt-2">
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging
                      ? "border-blue-400 bg-blue-50"
                      : "border-gray-300 bg-white"
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 mb-2">
                    {isDigitizing
                      ? t(
                          "app.clinical.digitizeUploadHint",
                          "Fotografiá o escaneá las páginas de la historia clínica física y subilas acá"
                        )
                      : t(
                          "app.clinical.uploadHint",
                          "Arrastra imágenes aquí o haz clic para seleccionar"
                        )}
                  </p>
                  <Input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="hidden"
                    id="image-upload"
                  />
                  <Button
                    variant="outline"
                    onClick={() =>
                      document.getElementById("image-upload").click()
                    }
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Seleccionar Imágenes
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-sm text-gray-500 mt-2">
                  Formatos soportados: JPG, PNG, GIF, WebP. Máximo 10MB por
                  imagen.
                </p>
              </div>

              {/* Vista previa de imágenes */}
              {images.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">{t("app.clinical.selectedImages", "Imágenes seleccionadas:")}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={
                            image.url || "/placeholder.svg?height=128&width=128"
                          }
                          alt={`Imagen ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeImage(index)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          {image.filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-4 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  saving
                  || loadingPatient
                  || (isDigitizing ? images.length === 0 : !notes.trim())
                }
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("app.profile.saving", "Guardando...")}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isDigitizing
                      ? t("app.clinical.digitizeSave", "Guardar digitalización")
                      : t("app.clinical.saveHistory", "Guardar Historia Clínica")}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <TreatmentConsumptionDialog
        treatment={selectedTreatment}
        open={showConsumption}
        saving={saving}
        onCancel={() => setShowConsumption(false)}
        onConfirm={(items) => doSave(items)}
      />
    </div>
  );
}
